import { useState, useEffect, ChangeEvent, useMemo } from "react";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IOrder from "../../../models/IOrder";
import OwButton from "../../shared/OwButton/OwButton";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import "./ProductGroupViewer.scss";
import IGroupItem from "../../../models/IGroupItem";
import UIService from "../../../services/UIService";
import { IAddBulkItems } from "@models/IAddBulkItems";
import { isNil, sortBy } from "lodash-es";

type Props = {
    orderModel: IOrder;
    productGroupModel: ICatalog_Item;
    addBulkItems: (items: IAddBulkItems[]) => void;
    hideTitle?: boolean;
    noInputPadding?: boolean;
    clearOnAdd?: boolean;
    orientation?: "horizontal" | "vertical";
};

interface IItemsToProcess {
    [key: string]: {
        item_guid: string;
        item_id: number;
        qty: string;
    };
}

const ProductGroupViewer = ({ orderModel, productGroupModel, addBulkItems, hideTitle, noInputPadding, clearOnAdd, orientation }: Props) => {
    const [itemsToProcess, setItemsToProcess] = useState<IItemsToProcess>({});
    const [groupItems, setGroupItems] = useState<IGroupItem[]>([]);
    const isHorizontal = useMemo(() => orientation !== "vertical", [orientation]);

    const setupItemsToProcess = () => {
        const { group_items } = productGroupModel;
        const itemsToProcess: IItemsToProcess = {};
        const groupItems = sortBy(group_items || [], ["group_sequence", "asc"]);

        for (const item of groupItems) {
            if (item.guid && item.id) {
                itemsToProcess[item.guid] = {
                    item_guid: item.guid,
                    item_id: item.id,
                    qty: "",
                };
            }
        }

        return { itemsToProcess, groupItems };
    };

    useEffect(() => {
        const { groupItems: newGroupItems, itemsToProcess: newItemsToProcess } = setupItemsToProcess();
        setGroupItems(newGroupItems);
        setItemsToProcess(newItemsToProcess);
    }, [productGroupModel]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>, guid: string) => {
        let value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const itemKeys = Object.keys(itemsToProcess);
        const updatedItemsToProcess = { ...itemsToProcess };

        for (const item of itemKeys) {
            if (item === guid) {
                if (name === "qty") {
                    const groupItem = groupItems.find((gi) => gi.guid === guid);

                    if (Number(value) < 0) {
                        value = "0";
                    }

                    if (groupItem) {
                        if (groupItem.has_inventory) {
                            if (!groupItem.allow_backorder) {
                                if (Number(value) > (groupItem.qty_on_hand || 0)) {
                                    value = groupItem.qty_on_hand?.toString() || "0";
                                }
                            }
                        }
                    }
                }

                if (name === "qty") {
                    updatedItemsToProcess[item].qty = value.toString();
                } else if (name === "item_guid") {
                    updatedItemsToProcess[item].item_guid = value.toString();
                } else if (name === "item_id") {
                    updatedItemsToProcess[item].item_id = Number(value);
                }
            }
        }

        setItemsToProcess(updatedItemsToProcess);
    };

    const addItems = () => {
        const itemKeys = Object.keys(itemsToProcess);
        const _itemsToProcess = itemsToProcess;
        const processedItems: { groupItem: IGroupItem; qty: number }[] = [];

        for (const key of itemKeys) {
            const item = _itemsToProcess[key];

            if (item.qty && Number(item.qty) > 0) {
                const groupItem = groupItems.find((groupItem) => {
                    if (groupItem.guid === item.item_guid) {
                        return true;
                    }
                });

                if (groupItem) {
                    processedItems.push({ groupItem, qty: Number(item.qty) });
                }
            }
        }

        const itemsToAdd: IAddBulkItems[] = processedItems.map((item) => ({
            item: {
                Catalog_Item: item.groupItem as ICatalog_Item,
                catalog_item_id: item.groupItem.id!,
                item_no: 0,
                qty: item.qty,
            },
            kitDetails: [],
            kitBuilderData: undefined,
            itemOptionValues: [],
        }));

        addBulkItems(itemsToAdd);

        if (clearOnAdd) {
            const { groupItems: newGroupItems, itemsToProcess: newItemsToProcess } = setupItemsToProcess();
            setItemsToProcess(newItemsToProcess);
        }
    };

    const getGroupItemData = (groupItem: IGroupItem, index: number) => {
        const guid = groupItem.guid || "";
        let qty = itemsToProcess[guid]?.qty || "";
        let disableQtyInput = false;
        let qtyInputClass = "";
        let max: number | undefined;

        if (groupItem.has_inventory) {
            if ((groupItem.qty_on_hand || 0) <= 0) {
                if (isNil(groupItem.allow_backorder) || !groupItem.allow_backorder) {
                    disableQtyInput = true;
                } else {
                    if (groupItem.allow_backorder) {
                        qtyInputClass = "bg-warning";
                    } else {
                        max = Math.max(0, groupItem.qty_on_hand || 0);
                    }
                }
            }
        }

        if (!groupItem.active) {
            disableQtyInput = true;
            max = 0;
            qty = "";
        }

        if (noInputPadding) {
            qtyInputClass += ` ps-0 pe-0`;
        }

        if (!isHorizontal) {
            qtyInputClass += ` w-7r`;
        }

        return {
            qtyInputClass,
            qty,
            disableQtyInput,
            max,
            guid,
        };
    };

    // Render functions for better organization
    const renderQtyInput = (groupItem: IGroupItem, index: number) => {
        const { qtyInputClass, qty, disableQtyInput, max, guid } = getGroupItemData(groupItem, index);

        console.log("max: ", max, guid);
        return (
            <OwInputWrapper
                className={"text-center no-border-radius " + qtyInputClass}
                name={"qty"}
                type={"number"}
                fn={(e: any) => handleChange(e, guid)}
                max={max}
                value={qty}
                disabled={disableQtyInput}
                onWheelFn={UIService.stopScrollWheel}
            />
        );
    };

    const renderInventory = (groupItem: IGroupItem) => (
        <div className={"inventory h-2r d-flex align-items-center justify-content-center"}>
            <div className={groupItem.has_inventory ? "" : "d-none"}>{groupItem.active ? groupItem.qty_on_hand : "0"}</div>
            <div className={groupItem.has_inventory ? "d-none" : ""}>--</div>
        </div>
    );

    const renderHorizontalLayout = () => (
        <>
            <thead>
                <tr className={"text-center"}>
                    {groupItems
                        .filter((groupItem) => groupItem.active)
                        .map((groupItem) => (
                            <th key={groupItem.guid}>{groupItem.group_title}</th>
                        ))}
                </tr>
            </thead>
            <tbody>
                <tr>
                    {groupItems
                        .filter((groupItem) => groupItem.active)
                        .map((groupItem, index) => (
                            <td key={groupItem.guid}>{renderQtyInput(groupItem, index)}</td>
                        ))}
                </tr>
                <tr>
                    {groupItems
                        .filter((groupItem) => groupItem.active)
                        .map((groupItem) => (
                            <td key={groupItem.guid}>{renderInventory(groupItem)}</td>
                        ))}
                </tr>
            </tbody>
        </>
    );

    const renderVerticalLayout = () => (
        <tbody>
            {groupItems
                .filter((groupItem) => groupItem.active)
                .map((groupItem, index) => (
                    <tr key={groupItem.guid}>
                        <th>{groupItem.group_title}</th>
                        <td>{renderQtyInput(groupItem, index)}</td>
                        <td>{renderInventory(groupItem)}</td>
                    </tr>
                ))}
        </tbody>
    );

    return (
        <div className={"ProductGroupViewer w-100"}>
            <table className={"table table-sm " + (UIService.isMainViewLight() ? "table-light" : "table-dark")}>
                {isHorizontal ? renderHorizontalLayout() : renderVerticalLayout()}
            </table>

            <div className={"col-auto"}>
                <OwButton
                    className={"btn-primary"}
                    fn={addItems}
                    title={"add"}
                    leftIconClass={"far fa-plus"}
                    disabled={Object.keys(itemsToProcess).length === 0}
                />
            </div>
        </div>
    );
};

export default ProductGroupViewer;
